import {useCallback, useEffect} from 'react';
import {create} from 'zustand';

import {useLatestRef} from 'lib/hooks/useLatestRef';

import type {TextProjection} from '../engine/annotationTextProjection';

/** An annotatable region mounted with note indicators, one entry per mount. */
export interface IndicatorRegion {
  regionId: string;
  container: HTMLElement;
  getProjection: () => TextProjection | null;
  /**
   * The language on screen ('es' when translated); null = original. Read when
   * measuring, like the projection: a new entry per swap would lose its re-measure.
   */
  getLang: () => string | null;
}

interface IndicatorRegionState {
  /** In mount order; read by AnnotationNoteIndicators to draw the margin buttons. */
  regions: IndicatorRegion[];
  register: (region: IndicatorRegion) => void;
  unregister: (region: IndicatorRegion) => void;
}

/**
 * Mount-owned registry of live DOM containers. Never wrap in devtools:
 * serializing a container walks its React internals and exhausts the tab's memory.
 */
export const useIndicatorRegionStore = create<IndicatorRegionState>()((set) => ({
  regions: [],

  register: (region: IndicatorRegion) => {
    set((state) => ({regions: [...state.regions, region]}));
  },

  // By entry identity, so a dual-mounted region's other mount stays registered.
  unregister: (region: IndicatorRegion) => {
    set((state) => {
      if (!state.regions.includes(region)) {
        return {};
      }
      return {regions: state.regions.filter((mounted) => mounted !== region)};
    });
  }
}));

/** Lists the region for indicator rendering while it is mounted; null regionId opts out. */
export const useIndicatorRegion = (
  regionId: string | null,
  container: HTMLElement | null,
  getProjection: () => TextProjection | null,
  lang: string | null
): void => {
  const register = useIndicatorRegionStore((state) => state.register);
  const unregister = useIndicatorRegionStore((state) => state.unregister);
  const langRef = useLatestRef(lang);
  const getLang = useCallback(() => langRef.current, [langRef]);

  useEffect(() => {
    if (!regionId || !container) {
      return undefined;
    }
    const region = {regionId, container, getProjection, getLang};
    register(region);
    return () => unregister(region);
  }, [regionId, container, getProjection, getLang, register, unregister]);
};
